import { useCallback, useRef } from 'react';
import { type NavigateOptions, useNavigate } from 'react-router-dom';

import useGlobalStore from 'hooks/useGlobalStore';

const useNavigation = () => {
    const reactRouterNavigate = useNavigate();

    const { getValue, setValue } = useGlobalStore({
        namespace: 'useNavigation'
    });

    const blockedNavigation = getValue('blockedNavigation');

    const unblockedNavigation = getValue('unblockedNavigation');

    const blockedPath = getValue('blockedPath');

    const isBlocked = getValue('isBlocked');

    // navigate() can be invoked from a callback created before the latest render
    // (a save handler awaiting its request, for example), so the blocked state is
    // read from a ref that always holds the newest value instead of the closure.
    const isBlockedRef = useRef(isBlocked);

    isBlockedRef.current = isBlocked;

    const navigate = useCallback(
        (path: string, options?: NavigateOptions) => {
            if (isBlockedRef.current) {
                setValue({ blockedNavigation: true });

                setValue({ blockedPath: path });

                return;
            }

            setValue({ blockedNavigation: false });

            reactRouterNavigate(path, options);
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const blockNavigation = () => {
        isBlockedRef.current = true;

        setValue({ isBlocked: true });
    };

    const unblockNavigation = () => {
        isBlockedRef.current = false;

        setValue({ blockedPath: null });

        setValue({ isBlocked: false });

        setValue({ blockedNavigation: false });

        setValue({ unblockedNavigation: true });
    };

    return {
        blockNavigation,
        blockedNavigation,
        blockedPath,
        isBlocked,
        navigate,
        unblockNavigation,
        unblockedNavigation
    };
};

export default useNavigation;
